<template>
  <div>
    <div v-if="label" class="my-2">
      <slot name="label" >
        <label for="file">{{ label }}</label>
      </slot>
    </div>
    <div class="v-input">
      <div
        v-if="$slots.prepend"
        class="v-input__prepend-outer"
      >
        <slot name="prepend" />
      </div>
      <FilePond
        id="multi-files-upload"
        ref="multiUpload"
        name="file"
        class="v-input__control"
        :label-idle="readonly?'':idleQuote"
        :accepted-file-types="accept"
        :max-file-size="maxFileSize"
        :max-files="maxFiles"
        :server="server"
        :files="filePondValue"
        :instant-upload="instantUpload"
        :style-item-panel-aspect-ratio="itemAspectRatio"
        :item-insert-location="itemInsertLocation"
        :file-validate-type-label-expected-types-map="typeWarnMsg"
        :style="cssVars"
        :disabled="readonly"
        credits="false"
        allow-multiple
        drop-validation
        @processfile="fileUpload"
        @removefile="emitOutput"
        @activatefile="activatefile"
        @reorderfiles="emitOutput"
        @updatefiles="emitOutput"
      />
    </div>
    <div
      v-debug
    >
      <VBtn @click="getDebugData">
        Fetch Test Files (Emulate Load)
      </VBtn>
      <VBtn @click="init">
        Refresh filepond
      </VBtn>
      <h2>dValue</h2>
      <p>{{ dValue }}</p>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import vueFilePond, { setOptions } from 'vue-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginMediaPreview from 'filepond-plugin-media-preview'
import FilePondPluginFilePoster from 'filepond-plugin-file-poster'
import DebugMixin from '@/mixins/DebugMixin'

import AtFormBaseInput from '@/mixins/AtFormBaseInput'
import LocaleMixin from '@/mixins/LocaleMixin'
// import zhCn from 'filepond/locale/zh-cn.js'

const FilePond = vueFilePond(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginFilePoster,
  FilePondPluginMediaPreview
)

function jsonEqual (a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * @emits upload-done {Boolean} - true if all files are uploaded, false otherwise
 */
export default {
  components: {
    FilePond
  },
  extends: AtFormBaseInput,
  mixins: [
    DebugMixin,
    LocaleMixin
  ],
  props: {
    // v-model value
    modelValue: {
      type: [Array, null],
      default: () => { return [] }
    },
    // sets input label
    label: {
      type: String,
      default: null
    },
    // accepted file types, seperated by comma
    accept: {
      required: false,
      type: String,
      default: null
    },
    // maximum file size allowable to upload, should be consistence with server settings
    maxFileSize: {
      type: String,
      default: '100MB'
    },
    // maximum file number allowable to upload
    maxFiles: {
      type: [Number, String],
      default: null,
      validator: val => !isNaN(val)
    },
    // allow mock files, images won't be downloaded and no preview is shown
    mock: {
      type: Boolean,
      default: false
    },
    // allow thumbnails for picture preview
    thumbnail: {
      type: Boolean,
      default: false
    },
    // enable instant upload of files
    instantUpload: {
      type: Boolean,
      default: true
    },
    // new uploads item insert location, available options: 'after', 'before'
    itemInsertLocation: {
      type: String,
      default: 'after'
    },
    // the aspect ratio for displaying items, set to null for dynamic display
    itemAspectRatio: {
      type: String,
      default: '0.56'
    },
    // the number of columns in each row
    columnNums: {
      type: Number,
      default: 4
    },
    // the max height for filepond element
    maxHeight: {
      type: String,
      default: ''
    },
    // file prompt messsage for users
    labelIdle: {
      type: String,
      default: null
    },
    // specifies key for the file uploaded
    fileKey: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // Thumbnail placeholders, replace when needed
      image_poster: require('@/assets/images/file/image_no_bg.png'),
      audio_poster: require('@/assets/images/file/audio_no_bg.png'),
      video_poster: require('@/assets/images/file/video_no_bg.png'),
      document_poster: require('@/assets/images/file/document_no_bg.png'),
      /// -------------------------------------------------------- ///
      resBuffer: [],
      server: {
        url: window.API_URL,
        process: {
          url: 'upload.api',
          method: 'POST',
          onload: (response) => {
            this.fileResponse(response)
          }
        },
        load: (source, load) => {
          fetch(source)
            .then((res) => res.blob())
            .then(load)
        },
        revert: null,
        restore: null
      },
      // fileFond value
      filePondValue: [],
      idleQuote: '<span class="filepond--label-action"> Click Here </span> or Drag & Drop your files',
      typeWarnMsg: {}
    }
  },
  computed: {
    cssVars () {
      if (this.maxFiles === 1) return null
      return {
        '--item-width': 100 / this.columnNums + '%',
        '--max-height': this.maxHeight
      }
    }
  },
  watch: {
    modelValue: {
      handler (val, oldVal) {
        if (jsonEqual(val, oldVal)) return
        this.init()
      },
      immediate: true,
      deep: true
    }
  },
  async mounted () {
    // console.log('AtFilePicker mounted')
    const filePondLocale = await import(`../../../../../../node_modules/filepond/locale/${this.convertLocale(this.currentLocale, 'vuefilepond').toLowerCase()}.js`)
    setOptions(filePondLocale.default)
    this.typeWarnMsg = { 'image/*': this.$t('components.AtFilePicker.image'), 'video/*': this.$t('components.AtFilePicker.video'), 'audio/*': this.$t('components.AtFilePicker.audio'), 'application/*': this.$t('components.AtFilePicker.application') }
    if (this.labelIdle != null) {
      this.idleQuote = this.labelIdle
    }
  },
  methods: {
    async getDebugData () {
      const res = await $this.$api.postData({
        data: [
          { api: 'E0000DemoService.AtFilePicker' }
        ]
      })
      this.dValue = res[0].output
    },
    // value to filepond value mapping
    init () {
      let files = []
      try {
        files = this.$refs.multiUpload.getFiles()
      } catch (e) {
        files = []
      }
      const _filePondValue = []
      if (this.modelValue !== null) {
        this.modelValue.forEach((item, index) => {
        // find the filepond item with the same id
          const file = files.find(file => file.id === item._id)
          if (file) {
            file.setMetadata('key', this.fileKey, true)
            _filePondValue.push(file)
          } else {
            _filePondValue.push({
              source: item.src,
              options: {
                type: 'local',
                metadata: { ...item }
              }
            })
            if (item.mimetype.startsWith('document') || item.mimetype.startsWith('application')) {
              _filePondValue[index].options.metadata.poster = this.document_poster
            }
          }
        })
      }

      this.filePondValue = _filePondValue
    },
    fileResponse (response) {
      this.resBuffer.push(JSON.parse(response))
    },
    fileUpload (err, file) {
      if (err == null) {
        const metadata = this.resBuffer.shift()
        const keys = Object.keys(metadata)

        for (const key of keys) {
          file.setMetadata(key, metadata[key], true)
        }
        file.setMetadata('key', this.fileKey, true)

        this.emitOutput()
      }
    },
    existsKey (arr, key) {
      if (arr) {
        return (key in arr)
      } else {
        return false
      }
    },
    activatefile (file) {
      let url = ''
      if (this.existsKey(file.getMetadata(), 'src')) {
        url = file.getMetadata('src')
      } else {
        throw new Error('File source not found')
      }
      const a = $('<a>')
        .attr('href', url)
        .attr('target', 'download="' + url + '"')
        .appendTo('body')
      a[0].click()
      a.remove()
    },
    emitOutput () {
      const result = []
      let flagReady = true
      const files = this.$refs.multiUpload.getFiles()
      this.filePondValue = files
      files.forEach((file, index) => {
        const meta = file.getMetadata()

        if (file.status === 9 || file.status === 3) { flagReady = false }

        delete meta.color
        delete meta.poster
        meta._id = file.id
        meta.order = index

        result.push(meta)
      })

      if (flagReady) {
        this.$emit('uploadDone', true)// ! deprecated
        this.$emit('isUploading', false)
        if (this.debug) this.$dialog.notify.info('All files are uploaded', { position: 'top-right' })
        this.dValue = result
      } else {
        if (this.debug) this.$dialog.notify.warning('Some files are still uploading', { position: 'top-right' })
        this.$emit('uploadDone', false) // ! deprecated
        this.$emit('isUploading', true)
      }
    }
  }
}
</script>

<style src="filepond/dist/filepond.min.css"></style>
<style src="filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css"></style>
<style src="filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css"></style>
<style src="filepond-plugin-media-preview/dist/filepond-plugin-media-preview.min.css"></style>

<style>
@media (min-width: 767px) {
  #multi-files-upload .filepond--item {
    width: calc(var(--item-width) - .5em);
  }
}

#multi-files-upload.filepond--root {
  max-height: var(--max-height);
}

.filepond--media-preview audio, .filepond--media-preview video{
  width: 100%;
  height: 100%;
}
/*
#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper {
  width: 100% !important;
  height: 100% !important;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper > .filepond--media-preview {
  width: 100% !important;
  height: 100% !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper > .filepond--media-preview > video {
  width: 100% !important;
  height: 100% !important;
}

[data-filepond-item-state=processing-complete] .filepond--item-panel {
    background-color: #263e46;
}
*/
</style>
